// Library methods
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// Components
import ProgressBar from "../../ProgressBar";
import Loader from "../../Loader";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  FileIcon,
} from "../../../../assets/icons";

// Utilities
import { useLiveUpdateStatusContext } from "../../../../contexts/LiveUpdateStatusContext";
import { getExamSectionText } from "../../../../utils/examReportsHelper";
import { examCardEnums } from "../../../../enums";
import { getComparator } from "../../../../utils/tableHelper";

const LiveUpdatePackageInfo = () => {
  // translations
  const { t } = useTranslation();

  const { exam, currentExamSection } = useLiveUpdateStatusContext();

  const allSortedSections = useMemo(() => {
    const enumValues = Object.values(examCardEnums);
    const examSectionNames = exam?.sectionTypeOrder?.map((examType) => {
      const enumValue = enumValues.find((value) => value.type === examType);
      return enumValue.sectionName;
    });

    const sortedExamSections = examSectionNames?.map((examSectionName) => {
      const sections = exam?.[examSectionName];
      const sortedSections = sections?.sort(getComparator("asc", ["order"]));
      return sortedSections;
    });

    return sortedExamSections?.flat();
  }, [exam]);

  const percentageCompleted = useMemo(() => {
    const numOfSections = allSortedSections?.length;
    const numOfCompletedSections = allSortedSections?.filter((section) =>
      Boolean(section?.completionDate)
    )?.length;

    return Math.floor((numOfCompletedSections / numOfSections) * 100);
  }, [allSortedSections]);

  const isCompleteExamSection = useCallback(
    (section) => Boolean(section?.completionDate),
    []
  );

  const isCurrentExamSection = useCallback(
    (section) => currentExamSection?.id === section?.id,
    [currentExamSection]
  );

  const getSectionIcon = useCallback(
    (section) => {
      if (isCurrentExamSection(section)) {
        return <Loader size="20px" width="24px" containerHeight="24px" />;
      } else if (isCompleteExamSection(section)) {
        return <CheckCircleIcon color="#22BE39" />;
      }
      return <Box width="24px" height="24px" />;
    },
    [isCurrentExamSection, isCompleteExamSection]
  );

  return (
    <div>
      <Accordion
        defaultExpanded
        sx={{
          border: "1px solid #5B6D7A40",
          boxShadow: "none",
          borderRadius: "8px !important",
        }}
      >
        <AccordionSummary
          id="multi-package-header"
          aria-controls="multi-package-content"
          expandIcon={<ChevronDownIcon color="#000" />}
          sx={{
            minHeight: "64px",
            height: "64px",
            padding: "16px",

            "& .MuiButtonBase-root": {
              minHeight: "fit-content",
              height: "fit-content",
            },
            "& .Mui-expanded": {
              minHeight: "24px",
              height: "24px",
            },
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            paddingRight="8px"
          >
            <Box display="flex" alignItems="center" gap="8px">
              <FileIcon color="#000" />
              <Typography fontSize="16px">{t("view_exam_package")}</Typography>
            </Box>
            <Typography fontSize="16px">
              {percentageCompleted}% {t("word_complete")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: "16px",
            borderTop: "1px solid #5B6D7A40",
          }}
        >
          <Box display="flex" flexDirection="column" gap="16px">
            <ProgressBar value={percentageCompleted} barColor="#48BECB" />
            <Box display="flex" flexDirection="column" gap="8px">
              {allSortedSections.map((examSection) => (
                <Box key={examSection?.id} display="flex" gap="8px">
                  {getSectionIcon(examSection)}
                  <Typography
                    fontSize="16px"
                    color={
                      isCompleteExamSection(examSection)
                        ? "#22BE39"
                        : isCurrentExamSection(examSection)
                          ? "#000"
                          : "#5B6D7A"
                    }
                  >
                    {getExamSectionText(examSection, t)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default LiveUpdatePackageInfo;
